import { Box } from "@mui/material";
import downloadjs from "downloadjs";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { downloadMimeType } from "ui/Scripts/source/models/consts";
import { ReportType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { IReportGenerated, ReportGenerated } from "source/models/report/reportGenerated";
import { IReportItem } from "source/models/report/reportItem";
import { IReportParameterValue } from "source/models/report/reportParameterValue";
import * as whistleBlowService from "source/models/services/whistleBlowService";
import { DownloadButton } from "source/components/framework/Button/DownloadButton/DownloadButton";
import { PageTitle } from "source/components/framework/Page/PageTitle";
import { ReportContainer } from "source/components/framework/ReportContainer";
import { IWhistleBlowGeneralFilter, WhistleBlowPageDashboardChart } from "../WhistleBlowPageDashboardChart";

// WhistleBlowReportList
export interface IWhistleBlowReportListProp {
    companyId: string;
    reports: IReportItem[];
    reportId: string;
    selectedStartDate: number;
    selectedEndDate: number;
    selectedUnitIds: string[];
    selectedCategoryIds: string[];
    selectedHandlerUserCodes: string[];
    selectedStateFilterId: string;
    selectedSeverityFilterId: string;
    selectedResolutionFilterId: string;
    selectedSourceFilterId: string;
    selectedAnonymityFilterId: string;
    selectedDynamicTimeFilterId: string;
    dataId?: string;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
}

interface IWhistleBlowReportListState extends IWhistleBlowGeneralFilter {
    isLoading: boolean;
    report: IReportItem;
    reportGenerated: IReportGenerated;
    reportDataUri: string;
}

export class WhistleBlowReportList extends React.Component<IWhistleBlowReportListProp, IWhistleBlowReportListState> {
    parameterValues: IReportParameterValue[] = [];

    constructor(props: IWhistleBlowReportListProp) {
        super(props);
        const report = props.reportId ? props.reports.find(i => i.id === props.reportId) : null;
        this.parameterValues = report ? report.getReportParameterValues() : [];
        const generalFilterValues = WhistleBlowPageDashboardChart.getGeneralFilterFromState(props);
        this.state = {
            ...generalFilterValues,
            isLoading: false,
            reportGenerated: null,
            reportDataUri: "",
            report,
        };
    }

    createReport = async (report: IReportItem, startDate: number, endDate: number, unitIds: string[], categoryIds: string[], handlerUserCodes: string[], stateFilterId: number, severityFilterId: number, resolutionFilterId: number,
        sourceFilterId: number, anonymityFilterId: number, dynamicTimeFilterId: number) => {
        if (!report) return;
        let reportGenerated: ReportGenerated = null;
        if (report.type !== ReportType.Html) {
            this.setState({ isLoading: true });
            reportGenerated = await AppUtils.callService(() => whistleBlowService.getReportGenerated(report.reportId, report.title, startDate, endDate, unitIds, categoryIds, handlerUserCodes,
                stateFilterId, severityFilterId, resolutionFilterId, sourceFilterId, anonymityFilterId, dynamicTimeFilterId, this.parameterValues), false, false, () => { this.setState({ isLoading: false }); });
            if (!reportGenerated) return;
        }
        const reportBlob = reportGenerated && reportGenerated.content ? Base.base64ToBlob(reportGenerated.content, reportGenerated.mediaType) : null;
        const reportDataUri = reportBlob ? await Base.blobToBase64(reportBlob) : "";
        this.setState({
            report,
            reportGenerated,
            reportDataUri,
            startDate,
            endDate,
            unitIds,
            categoryIds,
            handlerUserCodes,
            stateFilterId,
            severityFilterId,
            resolutionFilterId,
            sourceFilterId,
            anonymityFilterId,
            dynamicTimeFilterId
        });
    };

    componentDidMount(): void {
        const state = this.state;
        this.createReport(state.report, state.startDate, state.endDate, state.unitIds, state.categoryIds, state.handlerUserCodes, state.stateFilterId,
            state.severityFilterId, state.resolutionFilterId, state.sourceFilterId, state.anonymityFilterId, state.dynamicTimeFilterId);
    }

    componentDidUpdate(prevProps: IWhistleBlowReportListProp, prevState: IWhistleBlowReportListState) {
        const props = this.props;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevReportId = prevProps.reportId ? prevProps.reportId : "";
        const reportId = props.reportId ? props.reportId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const filterStateChange = WhistleBlowPageDashboardChart.getWhistleBlowGeneralFilterChange(prevProps, props);
        const report = props.reportId ? props.reports.find(i => i.id === props.reportId) : null;
        const parameterValues = report ? report.getReportParameterValues() : [];
        if (dataId === prevDataId && reportId === prevReportId && companyId === prevCompanyId &&
            filterStateChange.notChanged &&
            Base.equalStrArrays(prevProps.reports ? prevProps.reports.map(i => i.id) : [], props.reports ? props.reports.map(i => i.id) : []) &&
            JSON.stringify(this.parameterValues) === JSON.stringify(parameterValues)) return;
        this.parameterValues = parameterValues;
        this.createReport(reportId ? props.reports.find(i => i.id === reportId) : null, filterStateChange.startDate, filterStateChange.endDate, filterStateChange.unitIds, filterStateChange.categoryIds,
            filterStateChange.handlerUserCodes, filterStateChange.stateFilterId, filterStateChange.severityFilterId, filterStateChange.resolutionFilterId, filterStateChange.sourceFilterId,
            filterStateChange.anonymityFilterId, filterStateChange.dynamicTimeFilterId);
    }

    handleDownload = () => {
        const state = this.state;
        if (!state.reportGenerated || !state.reportGenerated.content) return;
        const blob = Base.base64ToBlob(state.reportGenerated.content, downloadMimeType);
        if (!blob) return;
        downloadjs(blob, state.reportGenerated.fileName);
    };

    render() {
        const state = this.state;
        const report = state.report;
        const reportGenerated = state.reportGenerated;
        return (
            <Box display="flex" flexDirection="column" flexGrow={1} position="relative">
                <PageTitle title={Translations.Reports + (report ? " - " + report.title : "")} position="absolute">
                    <DownloadButton onClick={this.handleDownload} disabled={!state.reportGenerated || state.isLoading}>
                        {report.type === ReportType.Pdf ? Translations.DownloadPdf : Translations.ExportToExcel}
                    </DownloadButton>
                </PageTitle>
                {(!!report && report.type !== ReportType.Html) &&
                    <ReportContainer variant="document">
                        {(!!reportGenerated && !!report && report.type === ReportType.Pdf) &&
                                <object style={{ margin: 0, padding: 0, width: 100 + "%", height: 100 + "%" }} type="application/pdf" data={state.reportDataUri + "#navpanes=0"} width="100%" height="100%"></object>
                        }
                        {(!!reportGenerated && !!report && report.type === ReportType.Excel) &&
                                <div></div>
                        }
                    </ReportContainer>
                }
                {(!!report && report.type === ReportType.Html) &&
                    <div></div>
                }
            </Box>
        );
    }
}